import { AdminRepository } from './admin.repository.js'
import { isString, isNonEmptyString } from '../../common/utils/type-guards.js'
import { BillingService } from '../billing/billing.service.js'
import { BillingRepository } from '../billing/billing.repository.js'
import { UsersRepository } from '../users/users.repository.js'
import { safeJsonParse } from '../../utils/sanitize.js'
import type {
  AdminDashboardStats,
  AdminUserListItem,
  AdminUserDetail,
  ModerationFlag,
  ModerationStats,
  CacheStats,
  CacheEntrySummary,
  AuditLogEntry,
  SystemHealthSnapshot,
  UserRole,
  UserStatus,
  AdminUserPlanInfo,
  AdminUserQuota,
  SubscriptionSummary
} from '@my-roadtrip/types'
import { redisClient } from '../../core/redis/client.js'
import { redisConfig } from '../../core/config/redisConfig.js'
import { auth, db } from '../../core/firebase/admin.js'
import type { AuthUser } from '../../core/auth/auth0.js'
import type { ListUsersQuery } from './admin.schemas.js'
import type { FirestoreDoc } from '../../common/types/firestore.js'
import { createLogger } from '../../common/utils/logger.js'
import { createBatchBuilder } from '../../common/utils/firestore-batch.js'

const logger = createLogger('AdminService')

type ResolveAction = 'approve' | 'hide' | 'suspend' | 'ban' | 'dismiss'

interface ResolveOptions {
  resolutionNote?: string
  suspendDays?: number
}

const STATS_CACHE_KEY = 'admin:stats'
const STATS_CACHE_TTL = 300 // 5 minutes to avoid recomputing heavy stats too often

export class AdminService {
  private readonly repository = new AdminRepository()
  private readonly billingService = new BillingService(
    new BillingRepository(db),
    new UsersRepository(db)
  )
  private statsPromise: Promise<AdminDashboardStats> | null = null
  private mapSubscriptionQuotaToAdmin(quota: SubscriptionSummary['quota'] | null | undefined): AdminUserQuota | null {
    if (!quota) return null
    const usage = quota.usage ?? { plansGenerated: 0, aiCalls: 0 }
    const limits = quota.limits ?? { plansPerMonth: 0, aiCallsPerMonth: 0, aiCallsPerDay: 0, aiRequestsPerDay: 0 }
    const aiCalls = usage.aiCalls ?? 0
    return {
      current: {
        plansGenerated: usage.plansGenerated ?? 0,
        aiCalls,
        aiRequests: aiCalls
      },
      limits: {
        plansPerMonth: limits.plansPerMonth ?? 0,
        aiCallsPerMonth: limits.aiCallsPerMonth ?? limits.aiCallsPerDay ?? limits.aiRequestsPerDay ?? 0,
        aiRequestsPerDay: limits.aiRequestsPerDay ?? limits.aiCallsPerDay ?? limits.aiCallsPerMonth ?? 0
      },
      window: quota.window
    }
  }

  async getDashboardStats(): Promise<AdminDashboardStats> {
    const cached = await this.readStatsFromCache()
    if (cached) {
      return cached
    }

    if (this.statsPromise) {
      return await this.statsPromise
    }

    this.statsPromise = this.buildDashboardStats()
    try {
      const stats = await this.statsPromise
      await this.writeStatsToCache(stats)
      return stats
    } finally {
      this.statsPromise = null
    }
  }

  private async buildDashboardStats(): Promise<AdminDashboardStats> {
    const [systemInfo, stats, billing] = await Promise.all([
      this.getSystemInfo(),
      this.repository.getDashboardStats(),
      this.billingService.getBillingMetrics().catch(error => {
        logger.warn('Failed to load billing metrics', { error })
        return null
      })
    ])

    if (billing) {
      stats.financial = {
        mrr: Math.round(billing.mrr * 100) / 100,
        totalRevenue: Math.round(billing.arr * 100) / 100,
        conversionRate: billing.conversionRate
      }
    }

    stats.system = systemInfo

    return stats
  }

  private async readStatsFromCache(): Promise<AdminDashboardStats | null> {
    try {
      const cached = await redisClient.get(STATS_CACHE_KEY)
      if (!cached) {
        return null
      }
      const parsed = safeJsonParse<AdminDashboardStats>(cached)
      return parsed ?? null
    } catch (error) {
      logger.warn('Failed to read stats cache', { error })
      return null
    }
  }

  private async writeStatsToCache(stats: AdminDashboardStats): Promise<void> {
    try {
      await redisClient.setex(STATS_CACHE_KEY, STATS_CACHE_TTL, JSON.stringify(stats))
    } catch (error) {
      logger.warn('Failed to write stats cache', { error })
    }
  }

  async listUsers(params: ListUsersQuery): Promise<{ users: AdminUserListItem[]; total: number; nextCursor?: string | null }> {
    const result = await this.repository.listUsers(params)
    const users = await Promise.all(
      result.users.map(async user => {
        try {
          const summary = await this.billingService.getSubscriptionSummary(user.id)
          if (!summary) return user
          const plan = {
            id: summary.plan.id,
            name: summary.plan.name,
            tier: summary.plan.tier
          }
          const quota = this.mapSubscriptionQuotaToAdmin(summary.quota) ?? user.quota
          const base: AdminUserListItem = {
            ...user,
            plan,
            quota
          }
          const subscriptionStatus = summary.subscription?.status ?? (user as any).subscriptionStatus ?? null
          return subscriptionStatus ? { ...base, subscriptionStatus } : base
        } catch (error) {
          logger.warn('Failed to overlay billing summary on admin user list item', { userId: user.id, error })
          return user
        }
      })
    )
    return { ...result, users }
  }

  async getUserDetails(userId: string): Promise<AdminUserDetail | null> {
    const user = await this.repository.getUserById(userId)
    if (!user) return null
    try {
      const summary = await this.billingService.getSubscriptionSummary(userId)
      if (!summary) return user
      const base = {
        ...user,
        plan: {
          id: summary.plan.id,
          name: summary.plan.name,
          tier: summary.plan.tier
        },
        quota: this.mapSubscriptionQuotaToAdmin(summary.quota) ?? user.quota
      }
      const subscriptionStatus = summary.subscription?.status ?? (user as any).subscriptionStatus ?? null
      return subscriptionStatus ? { ...base, subscriptionStatus } : base
    } catch (error) {
      logger.warn('Failed to overlay billing summary on admin user detail', { userId, error })
      return user
    }
  }

  async updateUserStatus(
    userId: string,
    status: UserStatus,
    actor: AuthUser,
    options?: { suspendedUntil?: string | null; suspensionReason?: string | null },
    ipAddress?: string
  ): Promise<void> {
    await this.repository.updateUserStatus(userId, status, options)
    await this.invalidateStatsCache()
    await this.recordAudit(actor, 'user_status_updated', { userId, status, ...options }, userId, ipAddress)
  }

  async updateUserRoles(userId: string, roles: UserRole[], actor: AuthUser, ipAddress?: string): Promise<void> {
    await this.repository.updateUserRoles(userId, roles)
    await this.invalidateStatsCache()
    await this.recordAudit(actor, 'user_roles_updated', { userId, roles }, userId, ipAddress)
  }

  async changeUserPlan(
    userId: string,
    plan: AdminUserPlanInfo,
    actor: AuthUser,
    ipAddress?: string,
    subscriptionStatus?: string | null
  ): Promise<void> {
    await this.billingService.resetQuotaForPlan(userId, { planId: plan.id, tier: plan.tier })
    await this.repository.changeUserPlan(userId, plan, subscriptionStatus)
    await this.invalidateStatsCache()
    await this.recordAudit(actor, 'user_plan_changed', { userId, plan, subscriptionStatus }, userId, ipAddress)
  }

  async resetUserQuota(userId: string, actor: AuthUser, ipAddress?: string): Promise<void> {
    await this.repository.resetUserQuota(userId)
    await this.invalidateStatsCache()
    await this.recordAudit(actor, 'user_quota_reset', { userId }, userId, ipAddress)
  }

  async purgeUserCache(userId: string, actor: AuthUser, ipAddress?: string): Promise<void> {
    const patterns = [`session:${userId}`, `ydoc:${userId}:*`, `plan:${userId}:*`, `user:${userId}:*`]
    for (const pattern of patterns) {
      const keys = await redisClient.keys(pattern)
      if (keys.length) {
        await redisClient.del(...keys)
      }
    }
    await this.recordAudit(actor, 'user_cache_purged', { userId }, userId, ipAddress)
  }

  async resetUserTokens(userId: string, actor: AuthUser, ipAddress?: string): Promise<void> {
    await auth.revokeRefreshTokens(userId)
    await this.recordAudit(actor, 'user_tokens_revoked', { userId }, userId, ipAddress)
  }

  async resendOnboarding(userId: string, actor: AuthUser, ipAddress?: string): Promise<void> {
    try {
      await db.collection('mail_queue').add({
        toUserId: userId,
        template: 'onboarding',
        createdAt: new Date(),
        meta: { triggeredBy: actor.uid }
      })
    } catch (error) {
      logger.warn('Failed to enqueue onboarding email', { userId, error })
      throw error
    }
    await this.recordAudit(actor, 'user_onboarding_email_queued', { userId }, userId, ipAddress)
  }

  /**
   * Set user activation status (production maintenance mode)
   * When activated=false, user cannot access the app in production (except auth sync endpoints)
   * This allows admins to control who can access the app during development/beta
   */
  async setUserActivation(userId: string, activated: boolean, actor: AuthUser, ipAddress?: string): Promise<void> {
    await db.collection('users').doc(userId).update({
      activated,
      activatedAt: activated ? new Date() : null,
      activatedBy: activated ? actor.uid : null,
      updatedAt: new Date()
    })
    await this.invalidateStatsCache()
    await this.recordAudit(
      actor,
      activated ? 'user_activated' : 'user_deactivated',
      { userId, activated },
      userId,
      ipAddress
    )
    logger.info(activated ? 'User activated' : 'User deactivated', { userId, activatedBy: actor.uid })
  }

  async getUserDiagnostic(userId: string): Promise<{
    user: AdminUserDetail | null
    plans: number
    auditLogs: AuditLogEntry[]
  }> {
    const user = await this.repository.getUserById(userId)

    let plans = 0
    try {
      const plansSnap = await db.collection('plans').where('userId', '==', userId).get()
      plans = plansSnap.size
    } catch (error) {
      logger.warn('Failed to count plans for diagnostic', { userId, error })
    }

    let auditLogs: AuditLogEntry[] = []
    try {
      const logs = await this.repository.getAuditLogs(100)
      auditLogs = logs.filter(
        (log) => log.actorId === userId || log.targetId === userId
      )
    } catch (error) {
      logger.warn('Failed to fetch audit logs for diagnostic', { userId, error })
    }

    return { user, plans, auditLogs }
  }

  async suspendUser(
    userId: string,
    durationDays: number,
    reason: string | undefined,
    actor: AuthUser,
    ipAddress?: string
  ): Promise<void> {
    const suspendedUntil = new Date(Date.now() + durationDays * 24 * 60 * 60 * 1000).toISOString()
    await this.repository.updateUserStatus(userId, 'suspended', {
      suspendedUntil,
      suspensionReason: reason ?? null
    })
    await this.invalidateStatsCache()

    try {
      await auth.revokeRefreshTokens(userId)
    } catch (error) {
      logger.warn('Failed to revoke sessions for user', { userId, error })
    }

    await this.recordAudit(
      actor,
      'user_suspended',
      { userId, durationDays, reason, suspendedUntil },
      userId,
      ipAddress
    )
  }

  async banUser(userId: string, reason: string | undefined, actor: AuthUser, ipAddress?: string): Promise<void> {
    await this.repository.updateUserStatus(userId, 'banned', {
      suspendedUntil: null,
      suspensionReason: reason ?? null
    })
    await this.invalidateStatsCache()

    try {
      await auth.revokeRefreshTokens(userId)
    } catch (error) {
      logger.warn('Failed to revoke sessions for user', { userId, error })
    }

    await this.recordAudit(actor, 'user_banned', { userId, reason }, userId, ipAddress)
  }

  async getModerationFlags(): Promise<ModerationFlag[]> {
    return await this.repository.getModerationFlags()
  }

  async getModerationStats(): Promise<ModerationStats> {
    return await this.repository.getModerationStats()
  }

  async resolveFlag(
    flagId: string,
    action: ResolveAction,
    actor: AuthUser,
    options?: ResolveOptions,
    ipAddress?: string
  ): Promise<ModerationFlag | null> {
    const resolutionMap: Record<ResolveAction, { status: ModerationFlag['status']; defaultMessage: string }> = {
      approve: { status: 'resolved', defaultMessage: 'Contenu approuvé, aucune violation constatée.' },
      hide: { status: 'resolved', defaultMessage: 'Contenu masqué suite à violation des règles.' },
      suspend: { status: 'resolved', defaultMessage: 'Utilisateur suspendu suite au signalement.' },
      ban: { status: 'resolved', defaultMessage: 'Utilisateur banni définitivement suite au signalement.' },
      dismiss: { status: 'dismissed', defaultMessage: 'Signalement classé sans suite.' }
    }

    const resolution = resolutionMap[action]
    if (!resolution) {
      throw new Error(`Unsupported moderation action: ${action}`)
    }

    const resolutionText = options?.resolutionNote?.trim()?.length
      ? options?.resolutionNote?.trim()
      : resolution.defaultMessage

    const flag = await this.repository.resolveFlag(flagId, {
      status: resolution.status,
      resolution: resolutionText,
      resolvedBy: actor.uid
    })

    if (!flag) {
      return null
    }

    if (action === 'suspend' && flag.reportedUserId) {
      const duration = options?.suspendDays ?? 7
      await this.suspendUser(flag.reportedUserId, duration, resolutionText, actor, ipAddress)
    }

    if (action === 'ban' && flag.reportedUserId) {
      await this.banUser(flag.reportedUserId, resolutionText, actor, ipAddress)
    }

    if (action === 'hide') {
      await this.hideContentForFlag(flag)
    }

    await this.invalidateStatsCache()
    await this.recordAudit(
      actor,
      'moderation_action',
      { flagId, action, resolution: resolutionText },
      flag.itemId,
      ipAddress
    )

    return flag
  }

  async getCacheStats(): Promise<CacheStats> {
    const keys = await redisClient.keys('*')
    const namespaces = new Set<string>()
    const entries: CacheEntrySummary[] = []
    let totalSize = 0

    for (const key of keys) {
      const namespace = key.includes(':') ? key.slice(0, key.indexOf(':')) : key
      namespaces.add(namespace)

      let size = 0
      try {
        const usage = typeof redisClient.memoryUsage === 'function' ? await redisClient.memoryUsage(key) : null
        if (typeof usage === 'number' && usage >= 0) {
          size = usage
          totalSize += size
        } else {
          const value = await redisClient.get(key)
          if (isString(value)) {
            size = Buffer.byteLength(value, 'utf8')
            totalSize += size
          } else {
            try {
              const buffer = await redisClient.getBuffer(key)
              if (buffer) {
                size = buffer.length
                totalSize += size
              }
            } catch (bufferError) {
              logger.warn('Failed to read binary cache entry', { key, error: bufferError })
            }
          }
        }
      } catch (error) {
        logger.warn('Failed to read cache entry', { key, error })
      }

      let ttl: number | null = null
      let expiresAt: string | null = null
      try {
        const rawTtl = await redisClient.ttl(key)
        if (typeof rawTtl === 'number' && rawTtl > 0) {
          ttl = rawTtl
          expiresAt = new Date(Date.now() + rawTtl * 1000).toISOString()
        }
      } catch (error) {
        logger.warn('Failed to read TTL for key', { key, error })
      }

      entries.push({
        namespace,
        key,
        size,
        ttl,
        expiresAt
      })
    }

    const sortedBySize = entries.slice().sort((a, b) => b.size - a.size)
    const sortedEntries = sortedBySize.slice(0, 50)

    return {
      hitRate: 0,
      size: totalSize,
      entries: keys.length,
      evictions: 0,
      namespaces: Array.from(namespaces),
      topEntries: sortedEntries
    }
  }

  async clearCache(namespace?: string, actor?: AuthUser, ipAddress?: string): Promise<number> {
    const pattern = namespace ? `${namespace}:*` : '*'
    const keys = await redisClient.keys(pattern)
    if (keys.length > 0) {
      await redisClient.del(...keys)
    }
    if (actor) {
      await this.recordAudit(actor, 'cache_cleared', { namespace, count: keys.length }, undefined, ipAddress)
    }
    return keys.length
  }

  async sweepCache(actor?: AuthUser, ipAddress?: string): Promise<number> {
    const keys = await redisClient.keys('*')
    const expired: string[] = []

    for (const key of keys) {
      try {
        const ttl = await redisClient.ttl(key)
        if (ttl === -2) {
          expired.push(key)
        }
      } catch (error) {
        logger.warn('Failed to fetch TTL for key', { key, error })
      }
    }

    if (expired.length > 0) {
      await redisClient.del(...expired)
    }

    if (actor) {
      await this.recordAudit(actor, 'cache_swept', { removed: expired.length }, undefined, ipAddress)
    }

    return expired.length
  }

  async deleteCacheEntry(key: string, actor?: AuthUser, ipAddress?: string): Promise<boolean> {
    const removed = await redisClient.del(key)
    if (actor) {
      await this.recordAudit(actor, 'cache_entry_deleted', { key }, undefined, ipAddress)
    }
    return removed > 0
  }

  async getAuditLogs(limit = 100): Promise<AuditLogEntry[]> {
    return await this.repository.getAuditLogs(limit)
  }

  async getSystemHealth(): Promise<SystemHealthSnapshot> {
    const startTime = Date.now()

    // Run all checks in parallel for speed
    const [
      database,
      cache,
      stripe,
      openai,
      auth0,
      mapbox,
      googlePlaces,
      weather,
      osrm,
      nominatim,
      overpass
    ] = await Promise.all([
      this.checkFirestore(),
      this.checkRedis(),
      this.checkStripe(),
      this.checkOpenAI(),
      this.checkAuth0(),
      this.checkMapbox(),
      this.checkGooglePlaces(),
      this.checkWeather(),
      this.checkOSRM(),
      this.checkNominatim(),
      this.checkOverpass()
    ])

    const totalTime = Date.now() - startTime

    const checks: SystemHealthSnapshot['checks'] = {
      timestamp: new Date().toISOString(),
      uptime: process.uptime(),
      database,
      cache,
      stripe,
      openai,
      auth0,
      mapbox,
      googlePlaces,
      weather,
      osrm,
      nominatim,
      overpass,
      memory: {
        used: process.memoryUsage().heapUsed,
        total: process.memoryUsage().heapTotal,
        percentage: process.memoryUsage().heapTotal
          ? Number(((process.memoryUsage().heapUsed / process.memoryUsage().heapTotal) * 100).toFixed(2))
          : 0
      },
      requests: {
        total: 0,
        errors: 0,
        errorRate: 0
      },
      checkDuration: totalTime
    }

    // Determine system status
    const criticalServices = [database, cache]
    const optionalServices = [stripe, openai, auth0, mapbox, googlePlaces, osrm, nominatim, overpass]

    let status: SystemHealthSnapshot['status'] = 'healthy'

    if (criticalServices.some(s => !s)) {
      status = 'unhealthy'
    } else if (optionalServices.filter(s => !s).length >= 3) {
      status = 'degraded'
    }

    const health: SystemHealthSnapshot = {
      status,
      checks
    }

    // Save to history (async, don't await)
    this.saveHealthHistory(health).catch(err =>
      logger.warn('Failed to save health history', { error: err })
    )

    return health
  }

  private async saveHealthHistory(health: SystemHealthSnapshot): Promise<void> {
    try {
      await db.collection('admin_health_history').add({
        ...health,
        timestamp: new Date()
      })

      // Keep only last 1000 entries (cursor-based)
      const keepThreshold = await db.collection('admin_health_history')
        .orderBy('timestamp', 'desc')
        .limit(1000)
        .get()

      if (keepThreshold.docs.length === 1000) {
        const oldestToKeep = keepThreshold.docs[999]
        const old = await db.collection('admin_health_history')
          .orderBy('timestamp', 'desc')
          .startAfter(oldestToKeep)
          .limit(100)
          .get()

        if (!old.empty) {
          const batch = createBatchBuilder(db)
          for (const doc of old.docs) {
            batch.deleteRef(doc.ref)
          }
          await batch.commit()
        }
      }
    } catch (error) {
      logger.warn('Failed to save health history', { error: error })
    }
  }

  async getSystemInfo(): Promise<any> {
    const os = await import('node:os')
    const redisType = redisConfig.driver === 'upstash' ? 'upstash' : 'local'
    const redisHost = redisType === 'upstash'
      ? redisConfig.upstashHost ?? redisConfig.upstashUrl ?? 'upstash'
      : redisConfig.redisHost
    
    let redisConnected = false
    try {
      await redisClient.ping()
      redisConnected = true
    } catch {
      redisConnected = false
    }

    return {
      nodeVersion: process.version,
      platform: process.platform,
      arch: process.arch,
      cpuCores: os.cpus().length,
      totalMemoryMB: Math.round(os.totalmem() / 1024 / 1024),
      freeMemoryMB: Math.round(os.freemem() / 1024 / 1024),
      uptimeSeconds: Math.round(process.uptime()),
      processMemoryMB: Math.round(process.memoryUsage().heapUsed / 1024 / 1024),
      redisType,
      redisHost,
      redisConnected,
      redisDriverSource: redisConfig.driverSource
    }
  }

  private async checkFirestore(): Promise<boolean> {
    try {
      await db.collection('_healthcheck').limit(1).get()
      return true
    } catch (error) {
      logger.warn('Firestore health check failed', { error: error })
      return false
    }
  }

  private async checkRedis(): Promise<boolean> {
    try {
      await redisClient.ping()
      return true
    } catch (error) {
      logger.warn('Redis health check failed', { error: error })
      return false
    }
  }

  private async checkStripe(): Promise<boolean> {
    if (!process.env.STRIPE_SECRET_KEY) {
      return false
    }

    try {
      // Import Stripe dynamically to avoid issues if not configured
      const Stripe = (await import('stripe')).default
      const stripe = new Stripe(process.env.STRIPE_SECRET_KEY, { apiVersion: '2024-06-20' })

      // Retrieve account info (free, no charge)
      const account = await stripe.accounts.retrieve()
      return Boolean(account.id)
    } catch (error) {
      logger.warn('Stripe health check failed', { error: error })
      return false
    }
  }

  private async checkOpenAI(): Promise<boolean> {
    if (!process.env.OPENAI_API_KEY) {
      return false
    }

    try {
      // Validate key format
      const key = process.env.OPENAI_API_KEY
      const isValidFormat = key.startsWith('sk-') && key.length > 20

      if (!isValidFormat) {
        return false
      }

      // Make minimal API call to verify key is active
      const response = await fetch('https://api.openai.com/v1/models', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${key}`,
          'Accept': 'application/json'
        },
        signal: AbortSignal.timeout(5000)
      })

      return response.ok
    } catch (error) {
      logger.warn('OpenAI health check failed', { error: error })
      return false
    }
  }

  private async checkAuth0(): Promise<boolean> {
    if (!process.env.AUTH0_DOMAIN) {
      return false
    }

    try {
      // Check if Auth0 domain is reachable (HTTPS check)
      const domain = process.env.AUTH0_DOMAIN
      const url = `https://${domain}/.well-known/openid-configuration`

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      })

      return response.ok
    } catch (error) {
      logger.warn('Auth0 health check failed', { error: error })
      return false
    }
  }

  private async checkMapbox(): Promise<boolean> {
    const token = process.env.MAPBOX_ACCESS_TOKEN || process.env.MAPBOX_TOKEN

    if (!token) {
      return false
    }

    try {
      // Validate token with Mapbox API (free endpoint)
      const url = `https://api.mapbox.com/v1/tokens/v2?access_token=${token}`

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      })

      return response.ok
    } catch (error) {
      logger.warn('Mapbox health check failed', { error: error })
      return false
    }
  }

  private async checkGooglePlaces(): Promise<boolean> {
    const key = process.env.GOOGLE_PLACES_API_KEY || process.env.VITE_GOOGLE_MAPS_API_KEY

    if (!key) {
      return false
    }

    try {
      // Test with zero-results query (free, no quota usage)
      const url = `https://maps.googleapis.com/maps/api/place/findplacefromtext/json?input=nonexistent&inputtype=textquery&key=${key}`

      const response = await fetch(url)
      const data = await response.json() as { status: string }

      // OK, ZERO_RESULTS, or INVALID_REQUEST means key works
      return ['OK', 'ZERO_RESULTS', 'INVALID_REQUEST'].includes(data.status)
    } catch (error) {
      logger.warn('Google Places health check failed', { error: error })
      return false
    }
  }

  private async checkOSRM(): Promise<boolean> {
    const url = process.env.VITE_OSRM_URL || 'https://router.project-osrm.org'

    try {
      // Test route endpoint (public, free)
      const testUrl = `${url}/route/v1/driving/13.388860,52.517037;13.397634,52.529407?overview=false&steps=false`

      const response = await fetch(testUrl, {
        headers: { 'Accept': 'application/json' }
      })

      return response.ok
    } catch (error) {
      logger.warn('OSRM health check failed', { error: error })
      return false
    }
  }

  private async checkNominatim(): Promise<boolean> {
    const url = process.env.NOMINATIM_URL || 'https://nominatim.openstreetmap.org/search'

    try {
      // Test search with minimal query (free, rate-limited but OK for health check)
      const testUrl = `${url}?q=Paris&format=json&limit=1`

      const response = await fetch(testUrl, {
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'MyRoadTrip-HealthCheck/1.0'
        }
      })

      return response.ok
    } catch (error) {
      logger.warn('Nominatim health check failed', { error: error })
      return false
    }
  }

  private async checkOverpass(): Promise<boolean> {
    const url = process.env.OVERPASS_URL || 'https://overpass-api.de/api/interpreter'

    try {
      // Test status endpoint (free, no query)
      const statusUrl = url.replace('/interpreter', '/status')

      const response = await fetch(statusUrl, {
        headers: { 'Accept': 'text/plain' }
      })

      return response.ok
    } catch (error) {
      logger.warn('Overpass health check failed', { error: error })
      return false
    }
  }

  private async checkWeather(): Promise<boolean> {
    const key = process.env.OPENWEATHER_API_KEY

    if (!key) {
      return false
    }

    try {
      // Test with a simple weather query (Paris coordinates)
      const url = `https://api.openweathermap.org/data/2.5/weather?lat=48.8566&lon=2.3522&appid=${key}`

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        signal: AbortSignal.timeout(5000)
      })

      if (!response.ok) {
        return false
      }

      const data = (await response.json()) as { cod?: number | string }
      return Number(data.cod) === 200
    } catch (error) {
      logger.warn('Weather API health check failed', { error: error })
      return false
    }
  }

  async getHealthHistory(hours = 24): Promise<SystemHealthSnapshot[]> {
    const since = new Date(Date.now() - hours * 60 * 60 * 1000)

    const snapshot = await db.collection('admin_health_history')
      .where('timestamp', '>=', since)
      .orderBy('timestamp', 'desc')
      .limit(500)
      .get()

    return snapshot.docs.map((doc: FirestoreDoc) => doc.data() as SystemHealthSnapshot)
  }

  async getHealthStats(hours = 24): Promise<{
    uptime: { percentage: number; totalChecks: number }
    services: Record<string, { uptime: number; failures: number }>
    averageCheckDuration: number
    incidents: Array<{ timestamp: string; service: string; duration: number }>
  }> {
    const history = await this.getHealthHistory(hours)

    if (history.length === 0) {
      return {
        uptime: { percentage: 100, totalChecks: 0 },
        services: {},
        averageCheckDuration: 0,
        incidents: []
      }
    }

    const services = [
      'database', 'cache', 'stripe', 'openai', 'auth0', 'mapbox',
      'googlePlaces', 'weather', 'osrm', 'nominatim', 'overpass'
    ]

    const stats: Record<string, { uptime: number; failures: number }> = {}
    const incidents: Array<{ timestamp: string; service: string; duration: number }> = []

    for (const service of services) {
      const total = history.length
      const failures = history.filter(h => !(h.checks as any)[service]).length
      const uptime = total > 0 ? ((total - failures) / total) * 100 : 100

      stats[service] = { uptime, failures }

      // Track incidents (service was down)
      for (let i = 0; i < history.length; i++) {
        const current = history[i]
        if (!(current.checks as any)[service]) {
          // Find duration of outage
          let duration = 1
          for (let j = i + 1; j < history.length; j++) {
            if ((history[j].checks as any)[service]) break
            duration++
          }

          incidents.push({
            timestamp: current.checks.timestamp,
            service,
            duration
          })
        }
      }
    }

    const avgDuration = history.reduce((sum, h) =>
      sum + ((h.checks as any).checkDuration || 0), 0
    ) / history.length

    return {
      uptime: {
        percentage: history.filter(h => h.status === 'healthy').length / history.length * 100,
        totalChecks: history.length
      },
      services: stats,
      averageCheckDuration: Math.round(avgDuration),
      incidents: incidents.slice(0, 10) // Last 10 incidents
    }
  }

  async getHealthSuggestions(): Promise<Array<{
    service: string
    issue: string
    suggestion: string
    priority: 'high' | 'medium' | 'low'
  }>> {
    const health = await this.getSystemHealth()
    const suggestions: Array<{
      service: string
      issue: string
      suggestion: string
      priority: 'high' | 'medium' | 'low'
    }> = []

    // Critical services
    if (!health.checks.database) {
      suggestions.push({
        service: 'Firestore',
        issue: 'Database connection failed',
        suggestion: 'Check Firebase credentials and network connectivity. Verify FIREBASE_PROJECT_ID is correct.',
        priority: 'high'
      })
    }

    if (!health.checks.cache) {
      suggestions.push({
        service: 'Redis',
        issue: 'Cache unavailable',
        suggestion: 'Verify Redis connection (REDIS_HOST, REDIS_PORT). Check if Upstash instance is active.',
        priority: 'high'
      })
    }

    // Payment service
    if (!health.checks.stripe) {
      suggestions.push({
        service: 'Stripe',
        issue: 'Payments unavailable',
        suggestion: 'Verify STRIPE_SECRET_KEY is valid. Check Stripe Dashboard status.',
        priority: 'high'
      })
    }

    // Optional services
    if (!health.checks.openai) {
      suggestions.push({
        service: 'OpenAI',
        issue: 'AI features disabled',
        suggestion: 'Add valid OPENAI_API_KEY starting with "sk-". Check OpenAI account billing.',
        priority: 'medium'
      })
    }

    if (!health.checks.googlePlaces) {
      suggestions.push({
        service: 'Google Places',
        issue: 'POI search limited',
        suggestion: 'Add GOOGLE_PLACES_API_KEY from Google Cloud logger. Enable Places API.',
        priority: 'medium'
      })
    }

    if (!health.checks.osrm && !health.checks.mapbox) {
      suggestions.push({
        service: 'Routing',
        issue: 'No routing service available',
        suggestion: 'Configure OSRM (free) or Mapbox (token required) for route calculations.',
        priority: 'medium'
      })
    }

    if (!health.checks.nominatim) {
      suggestions.push({
        service: 'Nominatim',
        issue: 'Geocoding unavailable',
        suggestion: 'OSM Nominatim may be rate-limited. Consider self-hosting or using alternative.',
        priority: 'low'
      })
    }

    // Performance suggestions
    if ((health.checks as any).checkDuration > 5000) {
      suggestions.push({
        service: 'System',
        issue: 'Health checks slow (>5s)',
        suggestion: 'Some APIs are slow to respond. Consider implementing timeout or circuit breaker.',
        priority: 'medium'
      })
    }

    if (health.checks.memory.percentage > 90) {
      suggestions.push({
        service: 'Memory',
        issue: `Memory usage high (${health.checks.memory.percentage}%)`,
        suggestion: 'Consider increasing server memory or optimizing memory-intensive operations.',
        priority: 'high'
      })
    }

    return suggestions
  }

  /**
   * Security alerts: spikes 401/403, ban/suspend bursts
   */
  async getSecurityAlerts(): Promise<Array<{
    id: string
    severity: 'critical' | 'high' | 'medium'
    title: string
    message: string
    timestamp: string
  }>> {
    const alerts: Array<{
      id: string
      severity: 'critical' | 'high' | 'medium'
      title: string
      message: string
      timestamp: string
    }> = []

    // Use health history request errorRate if present
    try {
      const history = await this.getHealthHistory(6)
      const recent = history.slice(0, 50)
      const withRequests = recent.filter(h => typeof (h.checks as any)?.requests?.errorRate === 'number')
      if (withRequests.length) {
        const avgErrorRate = withRequests.reduce((s, h) => s + ((h.checks as any).requests.errorRate as number), 0) / withRequests.length
        if (avgErrorRate > 0.05) {
          alerts.push({
            id: 'api-error-rate',
            severity: avgErrorRate > 0.15 ? 'critical' : 'high',
            title: 'Spike d\'erreurs API',
            message: `Taux d'erreur moyen ${Math.round(avgErrorRate * 100)}% sur les 6 dernières heures.`,
            timestamp: new Date().toISOString()
          })
        }
      }
    } catch {
      /* ignore */
    }

    // Detect ban/suspend bursts via audit logs
    try {
      const logs = await this.repository.getAuditLogs(200)
      const now = Date.now()
      const oneHour = 60 * 60 * 1000
      const recentActions = logs.filter((log) => {
        const ts = new Date(log.timestamp).getTime()
        return now - ts <= oneHour && typeof log.action === 'string'
      })
      const banCount = recentActions.filter(l => l.action.includes('ban')).length
      const suspendCount = recentActions.filter(l => l.action.includes('suspend')).length
      if (banCount >= 5 || suspendCount >= 8) {
        alerts.push({
          id: 'user-enforcement',
          severity: 'medium',
          title: 'Pic de suspensions/bans',
          message: `${banCount} bans, ${suspendCount} suspensions dans l'heure.`,
          timestamp: new Date().toISOString()
        })
      }
    } catch {
      /* ignore */
    }

    return alerts
  }

  async getHealthTrends(days = 7): Promise<{
    dailyStats: Array<{
      date: string
      uptime: number
      avgDuration: number
      incidents: number
      checks: number
      status: 'healthy' | 'degraded' | 'unhealthy'
    }>
    averageUptime: number
    averageDuration: number
    totalIncidents: number
    incidentChange: number
    uptimeTrend: 'improving' | 'degrading' | 'stable'
    performanceTrend: 'improving' | 'degrading' | 'stable'
    recentAverage: number
    recentDuration: number
    previousAverage: number
    previousDuration: number
  }> {
    const history = await this.getHealthHistory(days * 24)
    
    if (history.length === 0) {
      return {
        dailyStats: [],
        averageUptime: 0,
        averageDuration: 0,
        totalIncidents: 0,
        incidentChange: 0,
        uptimeTrend: 'stable',
        performanceTrend: 'stable',
        recentAverage: 0,
        recentDuration: 0,
        previousAverage: 0,
        previousDuration: 0
      }
    }

    // Group by day
    const dailyMap = new Map<string, SystemHealthSnapshot[]>()
    
    for (const snapshot of history) {
      const date = new Date(snapshot.checks.timestamp).toISOString().split('T')[0]
      if (!dailyMap.has(date)) {
        dailyMap.set(date, [])
      }
      dailyMap.get(date)!.push(snapshot)
    }

    // Calculate daily stats
    const dailyStats = Array.from(dailyMap.entries()).map(([date, snapshots]) => {
      const uptime = snapshots.filter(s => s.status === 'healthy').length / snapshots.length * 100
      const avgDuration = snapshots.reduce((sum, s) => 
        sum + ((s.checks as any).checkDuration || 0), 0
      ) / snapshots.length
      
      const incidents = snapshots.filter(s => s.status === 'unhealthy').length
      const checks = snapshots.length
      
      let status: 'healthy' | 'degraded' | 'unhealthy' = 'healthy'
      if (uptime < 90) status = 'unhealthy'
      else if (uptime < 99) status = 'degraded'

      return { date, uptime, avgDuration, incidents, checks, status }
    }).sort((a, b) => a.date.localeCompare(b.date))

    // Calculate overall averages
    const averageUptime = dailyStats.reduce((sum, d) => sum + d.uptime, 0) / dailyStats.length
    const averageDuration = dailyStats.reduce((sum, d) => sum + d.avgDuration, 0) / dailyStats.length
    const totalIncidents = dailyStats.reduce((sum, d) => sum + d.incidents, 0)

    // Calculate comparison (last 3 days vs previous 3 days)
    let recentAverage = 0
    let recentDuration = 0
    let previousAverage = 0
    let previousDuration = 0
    let incidentChange = 0
    let uptimeTrend: 'improving' | 'degrading' | 'stable' = 'stable'
    let performanceTrend: 'improving' | 'degrading' | 'stable' = 'stable'

    if (dailyStats.length >= 6) {
      const recent = dailyStats.slice(-3)
      const previous = dailyStats.slice(-6, -3)

      recentAverage = recent.reduce((sum, d) => sum + d.uptime, 0) / recent.length
      previousAverage = previous.reduce((sum, d) => sum + d.uptime, 0) / previous.length
      const uptimeChange = recentAverage - previousAverage

      recentDuration = recent.reduce((sum, d) => sum + d.avgDuration, 0) / recent.length
      previousDuration = previous.reduce((sum, d) => sum + d.avgDuration, 0) / previous.length
      const performanceChange = ((recentDuration - previousDuration) / previousDuration) * 100

      const recentIncidents = recent.reduce((sum, d) => sum + d.incidents, 0)
      const previousIncidents = previous.reduce((sum, d) => sum + d.incidents, 0)
      incidentChange = recentIncidents - previousIncidents

      if (uptimeChange > 2) uptimeTrend = 'improving'
      else if (uptimeChange < -2) uptimeTrend = 'degrading'

      if (performanceChange < -10) performanceTrend = 'improving'
      else if (performanceChange > 20) performanceTrend = 'degrading'
    }

    return {
      dailyStats,
      averageUptime,
      averageDuration,
      totalIncidents,
      incidentChange,
      uptimeTrend,
      performanceTrend,
      recentAverage,
      recentDuration,
      previousAverage,
      previousDuration
    }
  }

  async getServiceComparison(): Promise<Array<{
    service: string
    uptime24h: number
    uptime7d: number
    avgResponseTime: number
    lastIncident: string | null
    reliability: 'excellent' | 'good' | 'fair' | 'poor'
  }>> {
    const history24h = await this.getHealthHistory(24)
    const history7d = await this.getHealthHistory(7 * 24)

    const services = [
      { key: 'database', name: 'Firestore' },
      { key: 'cache', name: 'Redis' },
      { key: 'stripe', name: 'Stripe' },
      { key: 'openai', name: 'OpenAI' },
      { key: 'auth0', name: 'Auth0' },
      { key: 'mapbox', name: 'Mapbox' },
      { key: 'googlePlaces', name: 'Google Places' },
      { key: 'osrm', name: 'OSRM' },
      { key: 'nominatim', name: 'Nominatim' },
      { key: 'overpass', name: 'Overpass' }
    ]

    return services.map(({ key, name }) => {
      const uptime24h = history24h.length > 0
        ? (history24h.filter(h => (h.checks as any)[key]).length / history24h.length) * 100
        : 100

      const uptime7d = history7d.length > 0
        ? (history7d.filter(h => (h.checks as any)[key]).length / history7d.length) * 100
        : 100

      // Find last incident
      let lastIncident: string | null = null
      for (const snapshot of history7d) {
        if (!(snapshot.checks as any)[key]) {
          lastIncident = snapshot.checks.timestamp
          break
        }
      }

      // Calculate reliability score
      let reliability: 'excellent' | 'good' | 'fair' | 'poor' = 'excellent'
      if (uptime7d < 95) reliability = 'poor'
      else if (uptime7d < 98) reliability = 'fair'
      else if (uptime7d < 99.5) reliability = 'good'

      return {
        service: name,
        uptime24h: Math.round(uptime24h * 100) / 100,
        uptime7d: Math.round(uptime7d * 100) / 100,
        avgResponseTime: 0, // Can be calculated if we store individual check times
        lastIncident,
        reliability
      }
    })
  }

  private async hideContentForFlag(flag: ModerationFlag): Promise<void> {
    try {
      if (flag.type === 'plan') {
        await db.collection('plans').doc(flag.itemId).set({ isPublic: false, visibility: 'private' }, { merge: true })
      }

      if (flag.type === 'share') {
        await db.collection('shares').doc(flag.itemId).set(
          {
            moderationStatus: 'hidden',
            isActive: false
          },
          { merge: true }
        )
      }
    } catch (error) {
      logger.warn('Failed to hide content for flag', { flagId: flag.id, error })
    }
  }

  private async recordAudit(
    actor: AuthUser,
    action: string,
    details: Record<string, unknown>,
    targetId?: string,
    ipAddress?: string
  ): Promise<void> {
    await this.repository.logAudit({
      action,
      actorId: actor.uid,
      actorName: actor.name ?? actor.email ?? 'admin',
      details,
      targetId,
      ipAddress
    })
  }

  private async invalidateStatsCache(): Promise<void> {
    try {
      await redisClient.del(STATS_CACHE_KEY)
    } catch (error) {
      logger.warn('Failed to invalidate stats cache', { error: error })
    }
  }
}
